// Pure helpers for the token listing page: form <-> API mapping, client-side
// validation that mirrors the v1 contract, sponsorship state, and readable
// error text. Kept free of React so they are unit-testable.

import {
  CHAIN_BY_ID,
  CATEGORY_NAME,
  MAX_SPONSOR_DAYS,
  NOTES_MAX,
  PLACEMENTS,
  canonicalPlacement,
} from "./constants";

const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const COINGECKO_ID = /^[a-z0-9._-]{1,128}$/;
const DAY_MS = 24 * 60 * 60 * 1000;
const PLACEMENT_VALUES = PLACEMENTS.map((p) => p.value);

export const SPONSOR_FIELDS = ["sponsor_name", "sponsored_from", "sponsored_until", "sponsored_placements"];

export function emptyForm() {
  return {
    network_id: "",
    contract_address: "",
    coingecko_id: "",
    name: "",
    symbol: "",
    decimals: "",
    logo_url: "",
    status: "draft",
    verified: false,
    featured: false,
    featured_rank: "",
    categories: [],
    sponsor_name: "",
    sponsored_from: "",
    sponsored_until: "",
    sponsored_placements: [],
    notes: "",
  };
}

const str = (v) => (v === null || v === undefined ? "" : String(v));

// ISO timestamp -> value for <input type="datetime-local"> in the viewer's zone.
export function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// <input type="datetime-local"> value (viewer's zone) -> ISO UTC, or null.
export function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function listingToForm(l) {
  if (!l) return emptyForm();
  return {
    network_id: str(l.network_id),
    contract_address: str(l.contract_address),
    coingecko_id: str(l.coingecko_id),
    name: str(l.name),
    symbol: str(l.symbol),
    decimals: str(l.decimals),
    logo_url: str(l.logo_url),
    status: l.status || "draft",
    verified: Boolean(l.verified),
    featured: Boolean(l.featured),
    featured_rank: str(l.featured_rank),
    categories: Array.isArray(l.categories) ? [...l.categories] : [],
    sponsor_name: str(l.sponsor_name),
    sponsored_from: isoToLocalInput(l.sponsored_from),
    sponsored_until: isoToLocalInput(l.sponsored_until),
    // a listing saved before the placement rename loads as the new keys
    sponsored_placements: Array.isArray(l.sponsored_placements)
      ? [...new Set(l.sponsored_placements.map(canonicalPlacement).filter(Boolean))]
      : [],
    notes: str(l.notes),
  };
}

// Chain labels used by the weblanding token listing form -> network_id.
const SUBMISSION_CHAIN = {
  "robinhood chain": 27,
  ethereum: 1,
  base: 14,
  arbitrum: 4,
  solana: 6,
  "bnb chain": 2,
  bsc: 2,
  "binance smart chain": 2,
  avalanche: 5,
  "bitcoin cash": 16,
  polygon: 3,
  optimism: 25,
  bitcoin: 0,
  near: 24,
};

const firstString = (obj, keys) => {
  for (const k of keys) {
    const v = obj && obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

/**
 * Prefill the listing form from a weblanding "token" form submission. Only the
 * fields that plausibly map are copied; everything else stays empty for the
 * marketing lead to fill or look up. Returns { form, hints } where hints are
 * short notes to show above the form.
 */
export function submissionToForm(submission) {
  const data = (submission && submission.data) || {};
  const form = emptyForm();
  const hints = [];

  form.name = firstString(data, ["projectName", "tokenName", "name"]).slice(0, 80);
  form.symbol = firstString(data, ["tokenSymbol", "symbol", "ticker"]).slice(0, 20);
  form.contract_address = firstString(data, ["contractAddress", "contract_address", "address", "tokenAddress"]);

  const chainLabel = firstString(data, ["chain", "network", "blockchain"]);
  if (chainLabel) {
    const id = SUBMISSION_CHAIN[chainLabel.toLowerCase()];
    if (id !== undefined) form.network_id = String(id);
    else hints.push(`The submission names the chain "${chainLabel}", which is not in the supported list. Choose a chain before saving.`);
  }

  const logo = firstString(data, ["logoUrl", "logo_url", "logo"]);
  if (/^https:\/\//i.test(logo)) form.logo_url = logo;

  const website = firstString(data, ["website"]);
  const socials = firstString(data, ["socials"]);
  const noteParts = [];
  if (submission && submission.id) noteParts.push(`From form submission ${submission.id}.`);
  if (website) noteParts.push(`Website: ${website}`);
  if (socials) noteParts.push(`Socials: ${socials}`);
  form.notes = noteParts.join("\n").slice(0, NOTES_MAX);
  form.status = "draft";

  return { form, hints };
}

export function isEvmChain(networkId) {
  const c = CHAIN_BY_ID[String(networkId)];
  return Boolean(c && c.evm);
}

export function normaliseAddress(networkId, address) {
  const a = str(address).trim();
  if (a.toLowerCase() === "native") return "native";
  return isEvmChain(networkId) ? a.toLowerCase() : a;
}

const isInt = (s) => /^-?\d+$/.test(String(s).trim());

/**
 * Validate the form. Mirrors the contract's server-side rules so most mistakes
 * are caught before a round trip. Returns { field: message }; empty = valid.
 */
export function validateForm(form, { isEdit = false } = {}) {
  const e = {};

  if (!isEdit) {
    if (form.network_id === "" || !CHAIN_BY_ID[String(form.network_id)]) e.network_id = "Choose a chain.";
    const addr = str(form.contract_address).trim();
    if (!addr) e.contract_address = "Enter the contract address, or native for the chain's own coin.";
    else if (addr.toLowerCase() !== "native") {
      if (isEvmChain(form.network_id) && !EVM_ADDRESS.test(addr)) {
        e.contract_address = "EVM addresses are 0x followed by 40 hex characters.";
      } else if (/\s/.test(addr) || addr.length > 128) {
        e.contract_address = "The address cannot contain spaces and must be at most 128 characters.";
      }
    }
  }

  const name = str(form.name).trim();
  if (name.length < 1 || name.length > 80) e.name = "Name must be 1 to 80 characters.";

  const symbol = str(form.symbol).trim();
  if (symbol.length < 1 || symbol.length > 20 || /\s/.test(symbol)) e.symbol = "Symbol must be 1 to 20 characters, with no spaces.";

  const dec = str(form.decimals).trim();
  if (dec && (!isInt(dec) || Number(dec) < 0 || Number(dec) > 36)) e.decimals = "Decimals must be a whole number from 0 to 36.";

  const cg = str(form.coingecko_id).trim();
  if (cg && !COINGECKO_ID.test(cg)) e.coingecko_id = "Use the CoinGecko API id, lowercase, for example usd-coin.";

  const logo = str(form.logo_url).trim();
  if (logo) {
    let ok = false;
    try {
      ok = new URL(logo).protocol === "https:";
    } catch (_) {
      ok = false;
    }
    if (!ok) e.logo_url = "The logo must be an https URL.";
  }

  const cats = Array.isArray(form.categories) ? form.categories : [];
  if (cats.some((c) => !CATEGORY_NAME[c])) e.categories = "Pick categories from the list.";

  if (form.featured) {
    const r = str(form.featured_rank).trim();
    if (r && (!isInt(r) || Number(r) < 0 || Number(r) > 10000)) e.featured_rank = "Rank must be a whole number from 0 to 10000.";
  }

  if (hasSponsorship(form)) {
    const sn = str(form.sponsor_name).trim();
    if (!sn) e.sponsor_name = "Enter the sponsor's name.";
    else if (sn.length > 80) e.sponsor_name = "Sponsor name must be at most 80 characters.";
    const from = localInputToIso(form.sponsored_from);
    const until = localInputToIso(form.sponsored_until);
    if (!from) e.sponsored_from = "Choose when the campaign starts.";
    if (!until) e.sponsored_until = "Choose when the campaign ends.";
    if (from && until) {
      const span = new Date(until).getTime() - new Date(from).getTime();
      if (span <= 0) e.sponsored_until = "The end must be after the start.";
      else if (span > MAX_SPONSOR_DAYS * DAY_MS) e.sponsored_until = `A campaign can run for at most ${MAX_SPONSOR_DAYS} days.`;
    }
    const pl = Array.isArray(form.sponsored_placements) ? form.sponsored_placements : [];
    if (pl.length === 0) e.sponsored_placements = "Choose at least one placement.";
    else if (pl.some((p) => !PLACEMENT_VALUES.includes(p))) e.sponsored_placements = "Unknown placement.";
  }

  if (str(form.notes).length > NOTES_MAX) e.notes = `Notes must be at most ${NOTES_MAX} characters.`;

  return e;
}

export function hasSponsorship(form) {
  return Boolean(
    str(form.sponsor_name).trim() ||
      form.sponsored_from ||
      form.sponsored_until ||
      (Array.isArray(form.sponsored_placements) && form.sponsored_placements.length > 0),
  );
}

// Full AdminListingInput for create (actor is added by the admin proxy).
export function formToPayload(form) {
  const sponsored = hasSponsorship(form);
  return {
    network_id: Number(form.network_id),
    contract_address: normaliseAddress(form.network_id, form.contract_address),
    coingecko_id: str(form.coingecko_id).trim() || null,
    name: str(form.name).trim(),
    symbol: str(form.symbol).trim(),
    decimals: str(form.decimals).trim() === "" ? null : Number(str(form.decimals).trim()),
    logo_url: str(form.logo_url).trim() || null,
    status: form.status === "active" ? "active" : "draft",
    verified: Boolean(form.verified),
    featured: Boolean(form.featured),
    featured_rank: form.featured && str(form.featured_rank).trim() ? Number(form.featured_rank) : null,
    categories: Array.isArray(form.categories) ? [...form.categories] : [],
    sponsor_name: sponsored ? str(form.sponsor_name).trim() || null : null,
    sponsored_from: sponsored ? localInputToIso(form.sponsored_from) : null,
    sponsored_until: sponsored ? localInputToIso(form.sponsored_until) : null,
    sponsored_placements: sponsored && Array.isArray(form.sponsored_placements) ? [...form.sponsored_placements] : [],
    notes: str(form.notes).trim() || null,
  };
}

const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    const x = [...(a || [])].sort();
    const y = [...(b || [])].sort();
    return x.length === y.length && x.every((v, i) => v === y[i]);
  }
  return a === b;
};

const IDENTITY_FIELDS = ["network_id", "contract_address", "status"];

/**
 * Partial PATCH body: only fields that changed versus the loaded listing.
 * Identity and status are never sent (status moves through delist / relist).
 * If any sponsorship field changed, all four are sent so the server validates
 * the window as a whole (and clears the approval, per the contract).
 */
export function diffPayload(form, original) {
  const next = formToPayload(form);
  const prev = formToPayload(listingToForm(original));
  const out = {};
  for (const k of Object.keys(next)) {
    if (IDENTITY_FIELDS.includes(k)) continue;
    if (!sameValue(next[k], prev[k])) out[k] = next[k];
  }
  if (SPONSOR_FIELDS.some((k) => k in out)) {
    for (const k of SPONSOR_FIELDS) out[k] = next[k];
  }
  return out;
}

export function sponsorshipChanged(form, original) {
  const d = diffPayload(form, original);
  return SPONSOR_FIELDS.some((k) => k in d);
}

/**
 * Sponsorship state for display:
 *   none | pending (not approved) | scheduled (approved, not started) |
 *   active | ended (window passed).
 */
export function sponsorshipState(l, now = new Date()) {
  if (!l) return "none";
  const has = l.sponsor_name || l.sponsored_from || l.sponsored_until;
  if (!has) return "none";
  if (l.sponsored_active) return "active";
  const t = now.getTime();
  const until = l.sponsored_until ? new Date(l.sponsored_until).getTime() : null;
  const from = l.sponsored_from ? new Date(l.sponsored_from).getTime() : null;
  if (until !== null && t > until) return "ended";
  if (!l.sponsored_approved) return "pending";
  if (from !== null && t < from) return "scheduled";
  return "active";
}

export function shortAddress(a) {
  const s = str(a);
  if (!s) return "-";
  if (s === "native") return "native";
  return s.length > 16 ? `${s.slice(0, 8)}...${s.slice(-6)}` : s;
}

export function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

const ERROR_TEXT = {
  "already-listed": "This token is already listed on that chain. Open the existing listing instead.",
  "not-draft": "Only draft listings can be deleted. Delist this one instead.",
  "not-found": "This listing no longer exists. Refresh the list.",
  "listing-not-found": "This listing no longer exists. Refresh the list.",
  "not-listed": "Drafts are not listed, so they cannot be delisted. Delete the draft instead.",
  "already-active": "This listing is already active.",
  "sponsorship-ended": "This campaign has already ended, so it cannot be approved. Set a new window first.",
  "sponsorship-window-incomplete": "A sponsorship needs both a start and an end.",
  "sponsorship-window-invalid": "The sponsorship must end after it starts.",
  "invalid-network_id": "Choose a supported chain.",
  "invalid-contract_address": "The contract address is not valid for this chain.",
  "contract_address-required": "Enter the contract address, or native for the chain's own coin.",
  "network_id-or-coingecko_id-required": "Choose a chain and address, or enter a CoinGecko id, before looking up.",
  "invalid-coingecko_id": "The CoinGecko id is not valid.",
  "invalid-logo_url": "The logo must be an https URL of at most 512 characters.",
  "invalid-featured_rank": "The featured rank must be a whole number from 0 to 10000.",
  "invalid-sponsored_placements": "Choose Market › Tokens, Home holdings + token page, or both.",
  "invalid-sponsor_name": "Sponsor name must be at most 80 characters.",
  "invalid-sponsored_from": "The sponsorship start date is not valid.",
  "invalid-sponsored_until": "The sponsorship end date is not valid.",
  "invalid-status": "Status must be draft or active.",
  "invalid-notes": "Notes must be at most 5000 characters.",
  "internal-error": "The token listing service hit an error. Try again shortly.",
  forbidden: "You do not have permission for this action.",
  "unknown-chain": "Choose a supported chain.",
  "invalid-network-id": "Choose a supported chain.",
  "invalid-contract-address": "The contract address is not valid for this chain.",
  "invalid-address": "The contract address is not valid for this chain.",
  "invalid-name": "Name must be 1 to 80 characters.",
  "invalid-symbol": "Symbol must be 1 to 20 characters.",
  "invalid-decimals": "Decimals must be a whole number from 0 to 36.",
  "invalid-logo-url": "The logo must be an https URL.",
  "invalid-categories": "One of the categories is not supported.",
  "invalid-category": "One of the categories is not supported.",
  "invalid-featured-rank": "The featured rank is not valid.",
  "invalid-sponsorship-window": "The sponsorship must end after it starts and run for at most 30 days.",
  "sponsorship-window-too-long": "A sponsorship can run for at most 30 days.",
  "invalid-sponsored-placements": "Choose Market › Tokens, Home holdings + token page, or both.",
  "invalid-placements": "Choose Market › Tokens, Home holdings + token page, or both.",
  "coingecko-id-taken": "Another listing already uses this CoinGecko id.",
  "duplicate-coingecko-id": "Another listing already uses this CoinGecko id.",
  "invalid-coingecko-id": "The CoinGecko id is not valid.",
  "no-sponsorship": "This listing has no sponsorship to approve.",
  "already-approved": "This sponsorship is already approved.",
  "not-delisted": "Only delisted listings can be relisted.",
  "already-delisted": "This listing is already delisted.",
  "logo-too-large": "The logo must be 512 KB or smaller.",
  "logo-invalid-type": "The logo must be a PNG, JPEG or WEBP image.",
  "logo-missing": "Choose an image to upload.",
  "logo-upload-invalid": "The upload could not be read. Try a different file.",
  "logo-storage-not-configured": "Logo storage is not set up yet. Paste an https image URL instead.",
  "logo-upload-failed": "The logo could not be stored. Try again, or paste an https image URL.",
  "token-listings-upstream-unreachable": "The token listing service could not be reached. Try again shortly.",
  "lookup-failed": "The lookup could not be completed. Fill the fields by hand.",
};

const humanise = (s) => String(s).replace(/[_-]+/g, " ").trim();

// An API that predates the placement rename (BiorLabs_backend before
// 2026-10-28) answers invalid-sponsored_placements to the new keys. Returns the
// same body with the old keys, or null when there is nothing to retry.
export function legacyPlacementRetry(err, body) {
  const code = err && err.data && typeof err.data.error === "string" ? err.data.error : "";
  if (code !== "invalid-sponsored_placements" || !body || !Array.isArray(body.sponsored_placements)) return null;
  const OLD = { market_tokens: "market", home_holdings: "holdings" };
  if (!body.sponsored_placements.some((p) => OLD[p])) return null;
  return { ...body, sponsored_placements: body.sponsored_placements.map((p) => OLD[p] || p) };
}

/**
 * Turn a rejected request into a sentence for the UI. Never returns the raw
 * server payload. `err.data` is the response body (attached by api_helper).
 */
export function describeError(err, fallback = "Something went wrong. Try again.") {
  const data = (err && err.data) || {};
  const status = err && err.status;
  const code = typeof data.error === "string" ? data.error : typeof data.code === "string" ? data.code : "";

  if (code && ERROR_TEXT[code]) return ERROR_TEXT[code];
  const m = code.match(/^invalid[-_](.+)$/);
  if (m) return `The ${humanise(m[1])} value is not valid.`;

  if (status === 400 && data.success === false) return ERROR_TEXT["logo-invalid-type"];
  if (status === 400) return "Some fields are not valid. Check the form and try again.";
  if (status === 401) return "Your session has ended. Sign in again.";
  if (status === 403) return ERROR_TEXT.forbidden;
  if (status === 404) return ERROR_TEXT["not-found"];
  if (status === 409) return "This change conflicts with the current listing. Refresh and try again.";
  if (status === 413) return ERROR_TEXT["logo-too-large"];
  if (status === 503) return "The token listing service is not configured yet. Ask an engineer to set it up.";
  if (status === 502 || status === 504) return ERROR_TEXT["token-listings-upstream-unreachable"];
  if (status === undefined) return "No connection to the server. Check your network and try again.";
  return fallback;
}

/**
 * Which row actions to offer. Write actions need token_listings.manage;
 * sponsorship approve / revoke need token_listings.sponsor.
 *   edit     any listing
 *   publish  draft -> active (relist endpoint)
 *   delist   active
 *   relist   delisted
 *   delete   draft only (the server refuses others with not-draft)
 *   approve  sponsorship pending and its window not over
 *   revoke   sponsorship approved and not over
 */
export function rowActions(l, { canManage = false, canSponsor = false, now = new Date() } = {}) {
  const out = [];
  if (!l) return out;
  if (canManage) {
    out.push("edit");
    if (l.status === "draft") out.push("publish");
    if (l.status === "active") out.push("delist");
    if (l.status === "delisted") out.push("relist");
    if (l.status === "draft") out.push("delete");
  }
  const s = sponsorshipState(l, now);
  if (canSponsor) {
    if (s === "pending") out.push("approve");
    if (s === "scheduled" || s === "active") out.push("revoke");
  }
  return out;
}
